/**
 * A reusable people-search combobox — Task T2-B (pillar: manage people).
 *
 * Types into a text box; 250ms after the last keystroke, reads
 * `GET /employees?q=&status=active&limit=20` (`api/employees.ts`'s own header states
 * the permission/scope contract) and offers the answer as a listbox of
 * `full_name — department_name (email)`.
 *
 * ── No role-based logic here ─────────────────────────────────────────────────
 *
 * The same discipline `nav/menu.ts`'s boxed warning states for the menu: this
 * component adds no authorisation of its own. `GET /employees`'s resolved scope
 * (self ⊂ department ⊂ company) is the whole answer to "who appears here" — a plain
 * employee typing into this picker sees exactly one row, themself, and that is the
 * server's decision, not a branch in this file.
 *
 * ── Adoption ──────────────────────────────────────────────────────────────────
 *
 * Adopted first as this task's proof, in `PerformanceManagePanel`'s "goals for an
 * employee" subsection, replacing the numeric-id input it used to carry; adopted
 * again in `screens/admin/permissions/GrantForm.tsx`'s employee-subject picker
 * (Task T2-D).
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import {
  employeesPath,
  type EmployeeDirectoryRow,
  type EmployeeStatusFilter,
} from '../api/employees';
import { useApiQuery } from '../api/useApiQuery';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Icon } from '../states/icons';
import { Skeleton } from '../states/Skeleton';

/**
 * What the empty field says.
 *
 * At rest the box has to answer "what do I type here" — the other half of the
 * affordance the glyph gives, and the debt `DESIGN.md` §15 carried. It is a literal
 * rather than an `i18n/messages.ts` key because the catalogue has no key for it yet;
 * Vietnamese is the source language and English falls back to it key by key, so this
 * reads exactly as a Vietnamese-only catalogue entry would.
 */
const PLACEHOLDER = 'Tìm theo tên hoặc mã nhân sự…';

/** `web/shapes` convention: a search box waits for a pause before it asks the server. */
export const EMPLOYEE_PICKER_DEBOUNCE_MS = 250;

/** `openapi.yaml`'s `OffsetLimit` default is 50; a picker's own suggestion list is smaller. */
export const EMPLOYEE_PICKER_RESULT_LIMIT = 20;

export interface EmployeePickerValue {
  readonly id: string;
  readonly full_name: string;
  /**
   * The same person's **achau** id, carried beside the Workforce Pulse one because
   * some endpoints address people by the mirror's identifier rather than by ours —
   * `/devices`'s whole admin surface does (`src/modules/devices/admin.ts` calls it
   * `employee_id` and resolves it with `resolveEmployeeInScope`). Present whenever
   * this component produced the value; absent when a caller rebuilt one from a
   * stored id alone, which is why it is optional rather than required.
   */
  readonly achau_user_id?: string;
}

export interface EmployeePickerProps {
  readonly value: EmployeePickerValue | null;
  readonly onChange: (value: EmployeePickerValue | null) => void;
  readonly label: string;
  readonly disabled?: boolean;
  /** Overrides the picker's own default `status=active` filter. */
  readonly status?: EmployeeStatusFilter | undefined;
  /** Root `data-testid`; every element below it is `${testId}-<part>`. */
  readonly testId?: string;
}

interface EmployeesQueryResponse {
  readonly employees: readonly EmployeeDirectoryRow[];
}

export function EmployeePicker({
  value,
  onChange,
  label,
  disabled = false,
  status = 'active',
  testId = 'employee-picker',
}: EmployeePickerProps): ReactNode {
  const { t } = useI18n();
  const inputId = useId();
  const listboxId = useId();

  const [text, setText] = useState(value?.full_name ?? '');
  const [debouncedText, setDebouncedText] = useState(text);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef<number | null>(null);

  // The displayed text follows `value` only while the person is not actively
  // editing it — `open` is the proxy for "actively editing": closing the list (a
  // selection, `Escape`, or a blur) is what ends an edit and lets an outside reset
  // of `value` (a parent clearing the field) resync the text.
  useEffect(() => {
    if (!open) setText(value?.full_name ?? '');
  }, [value, open]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedText(text);
    }, EMPLOYEE_PICKER_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text]);

  useEffect(
    () => () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    },
    [],
  );

  const trimmed = debouncedText.trim();
  const listPath = open
    ? employeesPath({
        q: trimmed === '' ? undefined : trimmed,
        status,
        limit: EMPLOYEE_PICKER_RESULT_LIMIT,
      })
    : null;
  const list = useApiQuery<EmployeesQueryResponse>(listPath);
  const options = list.data?.employees ?? [];

  // The first row is highlighted the moment a fresh answer arrives, so `Enter`
  // alone (no `ArrowDown` first) selects the best match — the ordinary case.
  //
  // Adjusted **during render**, not in a `useEffect`: an effect fires one commit
  // after `list.data` changes, which is one render where the listbox is already on
  // screen with no highlighted option and `aria-activedescendant` names nothing —
  // exactly the state a screen reader would announce. Comparing against the last
  // seen `data` reference here means the highlight is correct in the very same
  // commit the new rows appear in (the pattern React's own docs call "adjusting
  // state during rendering").
  const [lastData, setLastData] = useState(list.data);
  if (list.data !== lastData) {
    setLastData(list.data);
    const nextOptions = list.data?.employees ?? [];
    setActiveIndex(nextOptions.length === 0 ? -1 : 0);
  }

  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;
  const activeOptionId =
    activeOption === undefined ? undefined : `${listboxId}-opt-${activeOption.id}`;

  function select(row: EmployeeDirectoryRow): void {
    onChange({ id: row.id, full_name: row.full_name, achau_user_id: row.achau_user_id });
    setText(row.full_name);
    setOpen(false);
    setActiveIndex(-1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((current) =>
        options.length === 0 ? -1 : Math.min(current + 1, options.length - 1),
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (options.length === 0 ? -1 : Math.max(current - 1, 0)));
    } else if (event.key === 'Enter') {
      const highlighted = open && activeIndex >= 0 ? options[activeIndex] : undefined;
      if (highlighted !== undefined) {
        event.preventDefault();
        select(highlighted);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div className="wp-field wp-picker" data-testid={testId}>
      <label className="wp-field__label" htmlFor={inputId}>
        {label}
      </label>
      {/* The same chrome `DepartmentSelect` wears (`.wp-select`): one 44px control with
          one 16px glyph inside its right edge, so the two filters read as siblings
          rather than as a control beside a bare text box. */}
      <div className="wp-picker__control">
        <input
          id={inputId}
          className="wp-field__input"
          type="text"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          {...(activeOptionId === undefined ? {} : { 'aria-activedescendant': activeOptionId })}
          autoComplete="off"
          placeholder={PLACEHOLDER}
          disabled={disabled}
          value={text}
          data-testid={`${testId}-input`}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // A short delay so a `mousedown` on an option (which blurs the input
            // first) still lands before the list unmounts. `select` also calls
            // `setOpen(false)` directly, so this is a no-op on that path.
            blurTimer.current = window.setTimeout(() => {
              setOpen(false);
            }, 150);
          }}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
            // Typing invalidates whatever was selected before — the picker must not
            // go on reporting a stale `{id, full_name}` while the text no longer
            // names it.
            if (value !== null) onChange(null);
          }}
          onKeyDown={onKeyDown}
        />
        <Icon name="search" className="wp-picker__glyph" />
      </div>
      {open ? (
        <div className="wp-picker__panel" data-testid={`${testId}-panel`}>
          {list.status === 'loading' ? (
            <Skeleton
              rows={2}
              variant="text"
              label={t('people.picker.loading')}
              testId={`${testId}-loading`}
            />
          ) : null}
          {list.status === 'error' && list.error !== null ? (
            <ApiErrorNote
              error={list.error}
              subject={t('people.picker.subject')}
              testId={`${testId}-error`}
            />
          ) : null}
          {list.status === 'ok' && options.length === 0 ? (
            <p className="wp-small wp-muted" data-testid={`${testId}-empty`}>
              {t('people.picker.empty')}
            </p>
          ) : null}
          {list.status === 'ok' && options.length > 0 ? (
            <ul
              className="wp-picker__list"
              role="listbox"
              id={listboxId}
              aria-label={label}
              data-testid={`${testId}-listbox`}
            >
              {options.map((row, index) => (
                <li
                  key={row.id}
                  id={`${listboxId}-opt-${row.id}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex
                      ? 'wp-picker__option wp-picker__option--active'
                      : 'wp-picker__option'
                  }
                  data-testid={`${testId}-option-${row.id}`}
                  onMouseDown={(event) => {
                    // `onMouseDown`, not `onClick`: it fires before the input's
                    // `onBlur` closes the list, so a click still lands on the row
                    // it was aimed at rather than on a list that just unmounted.
                    event.preventDefault();
                    select(row);
                  }}
                >
                  {row.full_name} — {row.department_name ?? t('people.noDepartment')} ({row.email})
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
